import { useEffect, useState } from 'react';
import uuid from 'uuid';
import { useParams, useHistory } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { chatgenieAPI } from 'src/config/api';
import useToast from 'src/hooks/useToast';
import { extractServerErrorMessage } from 'src/utils';
import queryKeys from 'src/constants/queryKeys';
import {
  getDefaultArgumentData,
  getDefaultReplyData,
  getDefaultVariationData
} from './Variation/utils';
import { argumentTypes } from './config';

export const useIntent = () => {
  const { intentId } = useParams();
  const [data, setData] = useState({
    name: '',
    description: '',
    variations: [getDefaultVariationData()],
    is_active: true
  });
  const [selectedVariationIndex, setSelectedVariationIndex] = useState(0);
  const [isDirty, setIsDirty] = useState(false);
  const variation = data.variations[selectedVariationIndex];
  const history = useHistory();
  const { toast } = useToast();

  const {
    data: res,
    isLoading,
    isFetching
  } = useQuery([queryKeys.GET_AGENTS, intentId], () => chatgenieAPI.getAgent(intentId), {
    enabled: !!intentId,
    refetchOnMount: true
  });
  const { mutate, isLoading: isUpserting } = useMutation(
    data => {
      if (intentId) {
        return chatgenieAPI.updateAgent({ ...data, id: intentId });
      }
      return chatgenieAPI.createAgent(data);
    },
    {
      onSuccess: () => {
        history.push({
          pathname: '/dashboard/magic/agent',
          state: { isSaved: true }
        });
      },
      onError: error => {
        const errorMessage = extractServerErrorMessage(error);
        toast.error(`Failed to ${intentId ? 'update' : 'create'} intent: ${errorMessage}`);
      }
    }
  );

  const updateIntent = newState => {
    setIsDirty(true);
    setData(newState);
  };

  const handleSubmit = e => {
    e.preventDefault();

    // Check for intersected ids in clients within variations
    const seenClientIds = new Set();
    const hasIntersectedClientIds = data.variations.some(variation => {
      return variation.clients.some(client => {
        if (seenClientIds.has(client.id)) {
          return true;
        }
        seenClientIds.add(client.id);
        return false;
      });
    });

    if (hasIntersectedClientIds) {
      toast.warn('Variations can not have same clients');
      return;
    }

    const normalizedData = {
      ...data,
      variations: data.variations.map(item => {
        const replies = item.replies.filter(reply => {
          if (!reply.id && !reply.description) {
            return false;
          }
          return true;
        });

        let variationArguments = item.arguments.map(arg => ({
          ...arg,
          id: typeof arg.id === 'string' ? undefined : arg.id,
          type: arg.type?.value || null,
          type_data: arg.type_data
            ? JSON.stringify(arg.type_data.filter(el => el.value).map(choice => choice.value))
            : null
        }));

        const hasEmptyArguments =
          variationArguments.length === 1 &&
          !variationArguments[0].name &&
          !variationArguments[0].description;

        if (hasEmptyArguments) {
          variationArguments = [];
        }

        return {
          ...item,
          id: typeof item.id === 'string' ? undefined : item.id,
          is_all_clients: item.is_default ? item.is_all_clients : false,
          approval: {
            ...item?.approval,
            contact_types: item?.approval?.contact_types?.map(type => type.id)
          },
          clients: item.clients.map(client => client.id),
          arguments: variationArguments,
          replies
        };
      })
    };

    mutate(normalizedData);
  };

  const updateSelectedVariation = callback => {
    updateIntent(prev => ({
      ...prev,
      variations: prev.variations.map((item, index) => {
        if (index === selectedVariationIndex) {
          return callback(item);
        }
        return item;
      })
    }));
  };

  const handleRemoveArgument = id => {
    const canArgumentsBeEmpty = variation.replies.some(el => el.description);
    if (variation.arguments.length === 1 && !canArgumentsBeEmpty) {
      toast.warn('There must be at least one form field or a reply set up.');
      return;
    }

    updateSelectedVariation(prev => ({
      ...prev,
      arguments: prev.arguments.filter(arg => arg.id !== id)
    }));
  };

  const handleChangeFormField = newArgumentdata => {
    updateSelectedVariation(prev => ({
      ...prev,
      arguments: prev.arguments.map(item =>
        item.id === newArgumentdata.id ? newArgumentdata : item
      )
    }));
  };

  const handleSelectVariation = index => setSelectedVariationIndex(index);

  const handleAddFormField = () => {
    updateSelectedVariation(prev => ({
      ...prev,
      arguments: [...prev.arguments, getDefaultArgumentData()]
    }));
  };

  const handleDescriptionChange = value => {
    updateIntent(prev => ({
      ...prev,
      description: value
    }));
  };

  const handleTitleChange = value => {
    updateIntent(prev => ({
      ...prev,
      name: value
    }));
  };

  const handleIsActive = isActive => {
    updateIntent(prev => ({
      ...prev,
      is_active: isActive
    }));
  };

  const handleChangeUrl = value => {
    updateSelectedVariation(prev => ({
      ...prev,
      url: value
    }));
  };

  const handleChangeApproval = value => {
    updateSelectedVariation(prev => ({
      ...prev,
      approval: {
        ...prev.approval,
        is_active: value
      }
    }));
  };

  const handleApprovalsCompanyTypes = value => {
    updateSelectedVariation(prev => ({
      ...prev,
      approval: {
        ...prev.approval,
        contact_types: value
      }
    }));
  };

  const handleVariationNameChange = value => {
    updateSelectedVariation(prev => ({
      ...prev,
      name: value,
      approval: {
        ...prev.approval,
        name: `Approval for: ${value}`
      }
    }));
  };

  const handleClientsChange = clients => {
    updateSelectedVariation(prev => ({
      ...prev,
      clients
    }));
  };

  const handleIsAllClientsChange = value => {
    updateSelectedVariation(prev => ({
      ...prev,
      clients: value ? [] : prev.clients,
      is_all_clients: value
    }));
  };

  const handleAddNewVariation = () => {
    updateIntent({
      ...data,
      variations: [
        ...data.variations,
        {
          ...getDefaultVariationData(),
          intent_id: data.id,
          id: uuid.v4(),
          is_default: false,
          name: `Variation ${data.variations.length}`,
          approval: {
            ...getDefaultVariationData().approval,
            name: `Approval for: Variation ${data.variations.length}`
          }
        }
      ]
    });
    setSelectedVariationIndex(data.variations.length);
  };

  const handleDeleteVariation = variationItem => {
    if (variationItem.is_default) {
      toast.error(`You could not delete default variation, try to delete whole intent`);
      return;
    }

    if (variationItem.id === variation.id) {
      setSelectedVariationIndex(data.variations.findIndex(item => item.is_default));
    }

    updateIntent({
      ...data,
      variations: data.variations.filter(item => item.id !== variationItem.id)
    });
  };

  const handleDuplicateVariation = variationItem => {
    const formFields = variationItem.arguments.map(arg => ({
      ...arg,
      id: uuid.v4()
    }));

    const duplicatedVariation = {
      ...variationItem,
      arguments: formFields,
      id: uuid.v4(),
      name: `${variationItem.name} (Copy)`,
      is_default: false
    };

    updateIntent({
      ...data,
      variations: [...data.variations, duplicatedVariation]
    });
    setSelectedVariationIndex(data.variations.length);
  };

  const handleReplyContentChange = (type, content) => {
    updateSelectedVariation(prev => ({
      ...prev,
      replies: prev.replies.map(reply =>
        reply.type === type
          ? {
              ...reply,
              ...content
            }
          : reply
      )
    }));
  };

  useEffect(() => {
    if (res) {
      setData({
        ...res,
        variations: res.variations.map(variation => ({
          ...variation,
          arguments: variation.arguments.map(arg => ({
            ...arg,
            type: argumentTypes.find(type => type.value === arg.type) || null,
            type_data: arg.type_data
              ? JSON.parse(arg.type_data).map(value => ({ value, id: uuid.v4() }))
              : null
          })),
          replies: variation.replies?.length ? variation.replies : getDefaultReplyData()
        }))
      });
    }
  }, [res]);

  return {
    data,
    isLoading: isLoading || isFetching,
    variation,
    isUpserting,
    isDirty,
    handleDuplicateVariation,
    handleDeleteVariation,
    handleAddNewVariation,
    handleVariationNameChange,
    handleClientsChange,
    handleIsAllClientsChange,
    handleChangeUrl,
    handleChangeApproval,
    handleApprovalsCompanyTypes,
    handleAddFormField,
    handleChangeFormField,
    handleDescriptionChange,
    handleRemoveArgument,
    handleSelectVariation,
    handleSubmit,
    handleTitleChange,
    handleIsActive,
    handleReplyContentChange
  };
};
